import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Users, Home, Search, Download, Phone, Mail, MapPin, Calendar, ChevronUp, ChevronDown, X, Filter, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGES = {
  nuovo:         { label: '🆕 Nuovo', cls: 'bg-blue-100 text-blue-800 border-blue-300' },
  contattato:    { label: '📞 Contattato', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  in_trattativa: { label: '🤝 In trattativa', cls: 'bg-violet-100 text-violet-800 border-violet-300' },
  convertito:    { label: '✅ Convertito', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  perso:         { label: '❌ Perso', cls: 'bg-rose-100 text-rose-800 border-rose-300' },
  scartato:      { label: '🗑️ Scartato', cls: 'bg-slate-100 text-slate-600 border-slate-300' },
};

const STATUSES = ['nuovo', 'contattato', 'in_trattativa', 'convertito', 'perso', 'scartato'];

export default function LeadsCRM() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('property'); // 'property' | 'partner'
  const [stats, setStats] = useState({ property: { total: 0, by_status: {} }, partner: { total: 0, by_status: {} } });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ q: '', status: 'all', language: '', variant: '', date_from: '', date_to: '' });
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedLead, setSelectedLead] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const token = localStorage.getItem('hw_token');
  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/leads/stats`, { headers: authHeaders });
      setStats(r.data);
    } catch (e) { /* silent */ }
  }, []); // eslint-disable-line

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: pageSize,
        sort_by: sortBy,
        sort_dir: sortDir,
        ...filters,
      };
      // Pulisci empty
      Object.keys(params).forEach(k => { if (!params[k] || params[k] === 'all') delete params[k]; });
      const r = await axios.get(`${API}/leads/${activeTab}`, { headers: authHeaders, params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      console.error(e);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [activeTab, page, sortBy, sortDir, filters, pageSize]); // eslint-disable-line

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
  };

  const handleDownloadCsv = async () => {
    const params = { ...filters };
    Object.keys(params).forEach(k => { if (!params[k] || params[k] === 'all') delete params[k]; });
    const qs = new URLSearchParams(params).toString();
    try {
      const r = await axios.get(`${API}/leads/${activeTab}/export.csv?${qs}`, { headers: authHeaders, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `lead_${activeTab === 'property' ? 'proprietari' : 'partner'}_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      alert('Errore download CSV');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <span className="text-slate-300 ml-1">↕</span>;
    return sortDir === 'asc' ? <ChevronUp className="inline w-3 h-3 ml-1"/> : <ChevronDown className="inline w-3 h-3 ml-1"/>;
  };

  const counters = stats[activeTab] || { total: 0, by_status: {} };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <button onClick={() => navigate('/dashboard')} className="text-xs text-slate-500 hover:text-slate-700 mb-1">← Dashboard</button>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                <Users className="w-6 h-6 text-rose-600"/>
                CRM Lead Proprietari & Partner
              </h1>
              <p className="text-sm text-slate-500 mt-1">Lead generati dai video WhatsApp post-soggiorno</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => navigate('/guest-videos')}
                className="text-sm px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 flex items-center gap-1.5"
                data-testid="leads-crm-back-to-videos"
              >📹 Video Campagne</button>
              <button
                onClick={handleDownloadCsv}
                className="text-sm px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-1.5"
                data-testid="leads-crm-export-csv"
              ><Download className="w-4 h-4"/> Export CSV</button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-4 border-b border-slate-200 -mb-px">
            <button
              onClick={() => { setActiveTab('property'); setPage(1); }}
              className={`px-5 py-2.5 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'property' ? 'border-rose-600 text-rose-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              data-testid="leads-crm-tab-property"
            >
              <Home className="w-4 h-4"/>
              Proprietari
              <span className="bg-rose-100 text-rose-800 text-xs px-2 py-0.5 rounded-full font-bold">{stats.property?.total || 0}</span>
              {stats.property?.new > 0 && (
                <span className="bg-blue-600 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">{stats.property.new} nuovi</span>
              )}
            </button>
            <button
              onClick={() => { setActiveTab('partner'); setPage(1); }}
              className={`px-5 py-2.5 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'partner' ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              data-testid="leads-crm-tab-partner"
            >
              <Users className="w-4 h-4"/>
              Partner
              <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full font-bold">{stats.partner?.total || 0}</span>
              {stats.partner?.new > 0 && (
                <span className="bg-blue-600 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">{stats.partner.new} nuovi</span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Counters bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
        <div className="flex flex-wrap gap-2">
          {STATUSES.map(s => {
            const count = counters.by_status?.[s] || 0;
            const isActive = filters.status === s;
            return (
              <button
                key={s}
                onClick={() => setFilters(f => ({ ...f, status: isActive ? 'all' : s }))}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${isActive ? STATUS_BADGES[s].cls + ' ring-2 ring-offset-1 ring-slate-400' : STATUS_BADGES[s].cls + ' opacity-70 hover:opacity-100'}`}
                data-testid={`leads-crm-filter-status-${s}`}
              >{STATUS_BADGES[s].label} <span className="ml-1 opacity-80">{count}</span></button>
            );
          })}
          <button
            onClick={() => setFilters(f => ({ ...f, status: 'all' }))}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${filters.status === 'all' ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-300'}`}
            data-testid="leads-crm-filter-status-all"
          >Tutti ({counters.total})</button>
        </div>
      </div>

      {/* Search + Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-3">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3">
          <div className="flex gap-2 flex-wrap items-center">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input
                type="text"
                value={filters.q}
                onChange={(e) => { setFilters(f => ({ ...f, q: e.target.value })); setPage(1); }}
                placeholder={activeTab === 'property' ? 'Cerca nome, email, telefono, comune, indirizzo…' : 'Cerca nome, email, telefono, comune…'}
                className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rose-500"
                data-testid="leads-crm-search"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm flex items-center gap-1.5 hover:bg-slate-50"
              data-testid="leads-crm-toggle-filters"
            ><Filter className="w-4 h-4"/> Filtri</button>
          </div>
          {showFilters && (
            <div className="mt-3 pt-3 border-t border-slate-200 grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div>
                <label className="text-[10px] text-slate-500 font-bold uppercase">Lingua</label>
                <select value={filters.language} onChange={e => setFilters(f => ({...f, language: e.target.value}))} className="w-full mt-0.5 border border-slate-300 rounded px-2 py-1.5 text-xs">
                  <option value="">Tutte</option>
                  <option value="it">Italiano</option>
                  <option value="en">English</option>
                  <option value="de">Deutsch</option>
                  <option value="fr">Français</option>
                  <option value="es">Español</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] text-slate-500 font-bold uppercase">Variante A/B/C</label>
                <select value={filters.variant} onChange={e => setFilters(f => ({...f, variant: e.target.value}))} className="w-full mt-0.5 border border-slate-300 rounded px-2 py-1.5 text-xs">
                  <option value="">Tutte</option>
                  <option value="A">A</option>
                  <option value="B">B</option>
                  <option value="C">C</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] text-slate-500 font-bold uppercase">Dal</label>
                <input type="date" value={filters.date_from} onChange={e => setFilters(f => ({...f, date_from: e.target.value}))} className="w-full mt-0.5 border border-slate-300 rounded px-2 py-1.5 text-xs"/>
              </div>
              <div>
                <label className="text-[10px] text-slate-500 font-bold uppercase">Al</label>
                <input type="date" value={filters.date_to} onChange={e => setFilters(f => ({...f, date_to: e.target.value}))} className="w-full mt-0.5 border border-slate-300 rounded px-2 py-1.5 text-xs"/>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-6">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="py-16 flex justify-center items-center text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin mr-2"/> Carico…
            </div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center text-slate-500">
              <div className="text-4xl mb-2">📭</div>
              <p className="text-sm">Nessun lead {activeTab === 'property' ? 'Proprietario' : 'Partner'} trovato</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid={`leads-crm-table-${activeTab}`}>
                <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-3 text-left cursor-pointer hover:bg-slate-100" onClick={() => handleSort('created_at')}>Data<SortIcon field="created_at"/></th>
                    <th className="px-3 py-3 text-left cursor-pointer hover:bg-slate-100" onClick={() => handleSort('status')}>Stato<SortIcon field="status"/></th>
                    <th className="px-3 py-3 text-left cursor-pointer hover:bg-slate-100" onClick={() => handleSort('full_name')}>Nome<SortIcon field="full_name"/></th>
                    <th className="px-3 py-3 text-left">Contatti</th>
                    {activeTab === 'property' ? (
                      <>
                        <th className="px-3 py-3 text-left cursor-pointer hover:bg-slate-100" onClick={() => handleSort('property_state')}>Stato<SortIcon field="property_state"/></th>
                        <th className="px-3 py-3 text-left cursor-pointer hover:bg-slate-100" onClick={() => handleSort('property_comune')}>Comune<SortIcon field="property_comune"/></th>
                        <th className="px-3 py-3 text-left">Indirizzo</th>
                      </>
                    ) : (
                      <>
                        <th className="px-3 py-3 text-left cursor-pointer hover:bg-slate-100" onClick={() => handleSort('guest_country')}>Paese<SortIcon field="guest_country"/></th>
                        <th className="px-3 py-3 text-left cursor-pointer hover:bg-slate-100" onClick={() => handleSort('guest_comune')}>Comune<SortIcon field="guest_comune"/></th>
                        <th className="px-3 py-3 text-center">Spoki</th>
                      </>
                    )}
                    <th className="px-3 py-3 text-center cursor-pointer hover:bg-slate-100" onClick={() => handleSort('variant')}>A/B/C<SortIcon field="variant"/></th>
                    <th className="px-3 py-3 text-center cursor-pointer hover:bg-slate-100" onClick={() => handleSort('language')}>Lingua<SortIcon field="language"/></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map(lead => (
                    <tr
                      key={lead.id}
                      onClick={() => setSelectedLead(lead)}
                      className="hover:bg-rose-50/40 cursor-pointer transition-colors"
                      data-testid={`leads-crm-row-${lead.id}`}
                    >
                      <td className="px-3 py-3 text-xs text-slate-600 whitespace-nowrap">
                        {new Date(lead.created_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td className="px-3 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold border ${STATUS_BADGES[lead.status]?.cls || 'bg-slate-100 text-slate-700'}`}>
                          {STATUS_BADGES[lead.status]?.label || lead.status}
                        </span>
                      </td>
                      <td className="px-3 py-3 font-semibold text-slate-800">{lead.full_name || '—'}</td>
                      <td className="px-3 py-3 text-xs text-slate-600">
                        <div className="flex items-center gap-1"><Mail className="w-3 h-3"/> {lead.email || '—'}</div>
                        <div className="flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3"/> {lead.phone || '—'}</div>
                      </td>
                      {activeTab === 'property' ? (
                        <>
                          <td className="px-3 py-3 text-xs"><strong>{lead.property_state}</strong></td>
                          <td className="px-3 py-3 text-xs">{lead.property_comune}</td>
                          <td className="px-3 py-3 text-xs text-slate-600 max-w-[200px] truncate" title={lead.property_address}>{lead.property_address}</td>
                        </>
                      ) : (
                        <>
                          <td className="px-3 py-3 text-xs"><strong>{lead.guest_country}</strong></td>
                          <td className="px-3 py-3 text-xs">{lead.guest_comune}</td>
                          <td className="px-3 py-3 text-center">
                            {lead.spoki_triggered ? <span title="Trigger inviato" className="text-emerald-600">✓</span> : <span className="text-slate-300">—</span>}
                          </td>
                        </>
                      )}
                      <td className="px-3 py-3 text-center">
                        <span className="inline-block bg-slate-100 text-slate-700 text-[10px] font-bold px-1.5 py-0.5 rounded">{lead.variant || '—'}</span>
                      </td>
                      <td className="px-3 py-3 text-center text-xs uppercase">{lead.language || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {total > pageSize && (
            <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between text-sm">
              <span className="text-slate-600">Pag. {page} di {Math.ceil(total / pageSize)} · {total} lead totali</span>
              <div className="flex gap-2">
                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 border border-slate-300 rounded text-xs disabled:opacity-40">← Prec.</button>
                <button disabled={page >= Math.ceil(total / pageSize)} onClick={() => setPage(p => p + 1)} className="px-3 py-1 border border-slate-300 rounded text-xs disabled:opacity-40">Succ. →</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Detail drawer */}
      {selectedLead && (
        <LeadDetailDrawer
          lead={selectedLead}
          leadType={activeTab}
          onClose={() => setSelectedLead(null)}
          onUpdated={() => { fetchLeads(); fetchStats(); }}
          authHeaders={authHeaders}
        />
      )}
    </div>
  );
}

/** Drawer dettaglio + update status + note + (V4) trigger Spoki */
function LeadDetailDrawer({ lead, leadType, onClose, onUpdated, authHeaders }) {
  const [data, setData] = useState(lead);
  const [newStatus, setNewStatus] = useState(lead.status);
  const [newNote, setNewNote] = useState('');
  const [lostReason, setLostReason] = useState(lead.lost_reason || '');
  const [saving, setSaving] = useState(false);
  const [triggeringSpoki, setTriggeringSpoki] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (newStatus !== data.status) payload.status = newStatus;
      if (newNote.trim()) payload.note = newNote.trim();
      if (newStatus === 'perso' && lostReason !== data.lost_reason) payload.lost_reason = lostReason;
      if (!Object.keys(payload).length) { setSaving(false); return; }
      const r = await axios.patch(`${API}/leads/${leadType}/${lead.id}`, payload, { headers: authHeaders });
      setData(r.data);
      setNewNote('');
      onUpdated && onUpdated();
    } catch (e) {
      alert('Errore: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const handleTriggerSpoki = async () => {
    if (!window.confirm('Marca questo lead come triggered su Spoki?')) return;
    setTriggeringSpoki(true);
    try {
      const r = await axios.post(`${API}/leads/partner/${lead.id}/spoki-trigger`, {}, { headers: authHeaders });
      setData(r.data.lead);
      onUpdated && onUpdated();
    } catch (e) {
      alert('Errore: ' + (e.response?.data?.detail || e.message));
    } finally {
      setTriggeringSpoki(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-slate-900/60 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-lg h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-testid="leads-crm-detail-drawer">
        <div className={`px-5 py-4 sticky top-0 z-10 text-white flex items-center justify-between ${leadType === 'property' ? 'bg-gradient-to-r from-rose-600 to-pink-600' : 'bg-gradient-to-r from-blue-700 to-indigo-600'}`}>
          <div>
            <h2 className="text-lg font-bold">{data.full_name}</h2>
            <p className="text-xs opacity-90">{leadType === 'property' ? 'Lead Proprietario (V3)' : 'Lead Partner (V4)'}</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="leads-crm-detail-close"><X className="w-6 h-6"/></button>
        </div>

        <div className="p-5 space-y-5 text-sm">
          {/* Stato */}
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-500">Stato</label>
            <select value={newStatus} onChange={e => setNewStatus(e.target.value)} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="leads-crm-detail-status">
              {STATUSES.map(s => <option key={s} value={s}>{STATUS_BADGES[s].label}</option>)}
            </select>
            {newStatus === 'perso' && (
              <input type="text" placeholder="Motivo perdita…" value={lostReason} onChange={e => setLostReason(e.target.value)} className="mt-2 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"/>
            )}
          </div>

          {/* Contatti */}
          <div className="bg-slate-50 rounded-lg p-3 space-y-1.5">
            <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-slate-500"/> <a href={`mailto:${data.email}`} className="text-blue-600 hover:underline">{data.email}</a></div>
            <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-500"/> <a href={`tel:${data.phone}`} className="text-blue-600 hover:underline">{data.phone}</a></div>
            {data.phone && <a href={`https://wa.me/${data.phone.replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer" className="text-emerald-600 hover:underline text-xs">💬 Apri su WhatsApp</a>}
          </div>

          {/* Specific data */}
          {leadType === 'property' ? (
            <div className="bg-rose-50 rounded-lg p-3 space-y-1.5">
              <h3 className="text-xs font-bold uppercase text-rose-700">🏡 Immobile da affidare</h3>
              <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-rose-600 mt-0.5"/>
                <div>
                  <div><strong>{data.property_comune}</strong> ({data.property_state})</div>
                  <div className="text-xs text-slate-600">{data.property_address}</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-blue-50 rounded-lg p-3 space-y-1.5">
              <h3 className="text-xs font-bold uppercase text-blue-700">🤝 Zona Partner</h3>
              <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-blue-600 mt-0.5"/>
                <div>
                  <div><strong>{data.guest_comune}</strong> ({data.guest_country})</div>
                  {data.has_experience !== null && data.has_experience !== undefined && (
                    <div className="text-xs text-slate-600">Esperienza case vacanza: <strong>{data.has_experience ? 'Sì' : 'No'}</strong></div>
                  )}
                  {data.experience_description && (
                    <div className="text-xs text-slate-600">Esperienza: <em>{data.experience_description}</em></div>
                  )}
                  {data.estimated_properties !== null && data.estimated_properties !== undefined && (
                    <div className="text-xs text-slate-600">Immobili stimati: <strong>{data.estimated_properties}</strong></div>
                  )}
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-blue-200">
                <div className="text-xs">Spoki automation: {data.spoki_triggered ? <span className="text-emerald-700 font-semibold">✓ Triggered ({data.spoki_triggered_at?.slice(0, 16)})</span> : <span className="text-amber-700 font-semibold">⏱️ Non ancora</span>}</div>
                <button
                  onClick={handleTriggerSpoki}
                  disabled={triggeringSpoki}
                  className="mt-2 w-full py-2 bg-blue-600 text-white rounded-lg text-xs font-semibold disabled:opacity-50"
                  data-testid="leads-crm-detail-spoki-trigger"
                >{triggeringSpoki ? '...' : (data.spoki_triggered ? '🔄 Re-trigger Spoki' : '▶️ Marca Spoki Triggered')}</button>
              </div>
            </div>
          )}

          {/* Contesto soggiorno */}
          <div className="bg-slate-50 rounded-lg p-3 space-y-1 text-xs">
            <h3 className="text-[10px] uppercase font-bold text-slate-500">Contesto Soggiorno</h3>
            {data.stay_property_name && <div>🏨 Struttura: <strong>{data.stay_property_name}</strong></div>}
            {data.booking_code && <div>📋 Prenotazione: <strong>{data.booking_code}</strong></div>}
            {data.check_in && <div>📅 Check-in: {data.check_in}</div>}
            {data.check_out && <div>📅 Check-out: {data.check_out}</div>}
            <div>🔤 Variante A/B/C: <strong>{data.variant || '—'}</strong> · Lingua: <strong>{(data.language || '').toUpperCase()}</strong></div>
          </div>

          {/* Notes timeline */}
          <div>
            <h3 className="text-xs font-bold uppercase text-slate-500 mb-2">Note interne</h3>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {(data.notes || []).slice().reverse().map(n => (
                <div key={n.id} className="bg-amber-50 border-l-2 border-amber-400 p-2 rounded-r text-xs">
                  <div className="text-slate-700">{n.text}</div>
                  <div className="text-[10px] text-slate-400 mt-1">{n.by} · {new Date(n.at).toLocaleString('it-IT')}</div>
                </div>
              ))}
              {(!data.notes || data.notes.length === 0) && <p className="text-xs text-slate-400 italic">Nessuna nota</p>}
            </div>
            <textarea
              value={newNote}
              onChange={e => setNewNote(e.target.value)}
              placeholder="Aggiungi una nota…"
              rows={2}
              className="mt-2 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500"
              data-testid="leads-crm-detail-note-input"
            />
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-3 bg-gradient-to-r from-rose-600 to-pink-600 text-white rounded-xl font-bold disabled:opacity-50"
            data-testid="leads-crm-detail-save"
          >{saving ? 'Salvo…' : '💾 Salva modifiche'}</button>
        </div>
      </div>
    </div>
  );
}
